import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useSeo } from "@/lib/seo";
import { money } from "@/lib/site";

const FOLLOWUP_LABELS = ["1 hour", "1 day", "3 days", "7 days", "14 days", "21 days"];
const STATUS_LABELS = {
  paid: ["PAID", "background:#d7ef8b;color:#123d30"],
  awaiting_payment: ["AWAITING PAYMENT", "background:#f7e9b8;color:#7a5b12"],
  awaiting_bank_transfer: ["BANK TRANSFER", "background:#e4ecd9;color:#123d30"],
  interest: ["INTEREST", "background:#eaeadd;color:#5c6b5a"],
};

const useAdminGuard = (navigate) => {
  const [admin, setAdmin] = useState(null);
  useEffect(() => {
    api.get("/admin/me").then(({ data }) => setAdmin(data)).catch(() => navigate("/admin"));
  }, [navigate]);
  return admin;
};

const StatCard = ({ label, value, sub, testid }) => (
  <div className="rounded-xl p-5" style={{ background: "#fbfbf4", border: "1px solid var(--line)" }} data-testid={testid}>
    <span className="eyebrow text-[9px] text-[#5c6b5a]">{label}</span>
    <strong className="mt-2 block font-display text-3xl">{value}</strong>
    {sub && <small className="mt-1 block text-[12px] text-[#5c6b5a]">{sub}</small>}
  </div>
);

const Th = ({ children }) => <th className="px-3 py-2.5 text-left text-[10px] font-extrabold tracking-[0.12em] text-[#5c6b5a]">{children}</th>;
const Td = ({ children, className = "" }) => <td className={`px-3 py-2.5 text-[13px] ${className}`}>{children}</td>;

function StatusBadge({ status }) {
  const [label, style] = STATUS_LABELS[status] || [status?.toUpperCase(), "background:#eaeadd;color:#5c6b5a"];
  return <span className="stat-chip" style={{ ...Object.fromEntries(style.split(";").map((s) => s.split(":"))), fontSize: 10 }}>{label}</span>;
}

export default function AdminPanel() {
  useSeo({ title: "Admin · Control room | OFIC 2026", noindex: true });
  const navigate = useNavigate();
  const admin = useAdminGuard(navigate);
  const [tab, setTab] = useState("overview");
  const [stats, setStats] = useState(null);
  const [registrations, setRegistrations] = useState([]);
  const [leads, setLeads] = useState([]);
  const [carts, setCarts] = useState([]);
  const [statusFilter, setStatusFilter] = useState("");
  const [query, setQuery] = useState("");
  const [detail, setDetail] = useState(null);

  const loadAll = () => {
    api.get("/admin/stats").then(({ data }) => setStats(data)).catch(() => {});
    api.get("/admin/registrations").then(({ data }) => setRegistrations(data.items)).catch(() => {});
    api.get("/admin/leads").then(({ data }) => setLeads(data.items)).catch(() => {});
    api.get("/admin/abandoned").then(({ data }) => setCarts(data.items)).catch(() => {});
  };

  useEffect(() => {
    if (admin) loadAll();
  }, [admin]); // eslint-disable-line react-hooks/exhaustive-deps

  const filteredRegistrations = useMemo(() => {
    let rows = registrations;
    if (statusFilter) rows = rows.filter((r) => r.status === statusFilter);
    if (query.trim()) {
      const q = query.trim().toLowerCase();
      rows = rows.filter((r) => [r.id, r.name, r.email, r.company, r.phone].some((v) => (v || "").toLowerCase().includes(q)));
    }
    return rows;
  }, [registrations, statusFilter, query]);

  if (!admin) return <div className="grid min-h-screen place-items-center bg-[#eaeadd]"><p className="animate-pulse text-sm font-bold text-[#5c6b5a]">Checking your session…</p></div>;

  const logout = async () => {
    await api.post("/admin/logout").catch(() => {});
    navigate("/admin");
  };
  const exportCsv = (path) => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/admin/${path}`, "_blank");
  const sendFollowup = async (cartId) => {
    try {
      await api.post(`/admin/abandoned/${cartId}/followup`);
      loadAll();
    } catch (err) {
      alert(formatApiErrorDetail(err.response?.data?.detail) || "Could not send the email.");
    }
  };

  const nextDue = (cart) => {
    const sent = (cart.followups_sent || []).length;
    if (sent >= 6) return "Sequence complete";
    return `Next: after ${FOLLOWUP_LABELS[sent]} from last activity`;
  };

  return (
    <div data-testid="admin-panel" className="min-h-screen bg-[#eaeadd]">
      <header className="sticky top-0 z-40 border-b bg-[#123d30]" style={{ borderColor: "rgba(243,243,233,.15)" }}>
        <div className="wrap flex h-16 items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <img src="/assets/ofic-mark.png" alt="OFIC 2026" className="h-9 w-auto" />
            <div>
              <p className="eyebrow text-[9px] text-[#d7ef8b]">Organiser control room</p>
              <p className="text-[13px] font-extrabold text-[#f3f3e9]">{admin.email}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/" className="hidden rounded-full px-4 py-2 text-[12px] font-bold text-[#d7ef8b] sm:block" data-testid="admin-view-site-link">View site ↗</Link>
            <button onClick={logout} data-testid="admin-logout-btn" className="rounded-full px-4 py-2 text-[12px] font-bold text-[#f3f3e9]" style={{ border: "1px solid rgba(243,243,233,.4)" }}>Sign out</button>
          </div>
        </div>
      </header>

      <nav className="wrap flex flex-wrap gap-2 py-5">
        {[["overview", "Overview"], ["registrations", "Registrations"], ["leads", "Leads"], ["carts", "Abandoned carts"]].map(([id, label]) => (
          <button key={id} data-testid={`admin-tab-${id}`} onClick={() => setTab(id)} className={`admin-tab ${tab === id ? "active" : ""}`}>{label}</button>
        ))}
      </nav>

      <main className="wrap pb-20">
        {tab === "overview" && stats && (
          <div data-testid="admin-overview">
            <div className="mb-5 flex flex-wrap items-center gap-2">
              <span className="stat-chip" style={{ background: "#fbfbf4", border: "1px solid var(--line)" }}>{stats.earlyBird ? "EARLY BIRD ACTIVE" : "STANDARD RATES"}</span>
              <span className="stat-chip" style={{ background: "#fbfbf4", border: "1px solid var(--line)" }}>{stats.paymentsReady ? "ONLINE PAYMENTS ON" : "ONLINE PAYMENTS OFF"}</span>
              <span className="stat-chip" style={{ background: "#fbfbf4", border: "1px solid var(--line)" }}>{stats.emailReady ? "EMAIL CONNECTED" : "EMAIL OFF"}</span>
              <span className="stat-chip" style={{ background: "#fbfbf4", border: "1px solid var(--line)" }}>GST {stats.gstPercent}%</span>
            </div>
            <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
              <StatCard testid="stat-registrations" label="Registrations" value={stats.registrations} sub={`${stats.paid} paid · ${stats.bank_transfer} bank transfer · ${stats.interest} interest`} />
              <StatCard testid="stat-revenue" label="Revenue (verified)" value={money(stats.revenue_inr, "INR")} sub={stats.revenue_usd ? `+ US$${stats.revenue_usd.toLocaleString("en-IN")} international` : "No international payments yet"} />
              <StatCard testid="stat-participants" label="Paid participants" value={stats.participants} sub="Across all packages" />
              <StatCard testid="stat-leads" label="Brochure leads" value={stats.leads} sub="Captured via download form" />
              <StatCard testid="stat-carts-open" label="Open abandoned carts" value={stats.abandoned_open} sub="People still to convert" />
              <StatCard testid="stat-carts-recovered" label="Recovered carts" value={stats.abandoned_recovered} sub="Finished registration after abandoning" />
              <StatCard testid="stat-early-badge" label="Early bird deadline" value="10 Nov 2026" sub="Standard rates apply from 11 Nov" />
            </div>
            <div className="mt-8 grid gap-6 lg:grid-cols-[1.4fr_1fr]">
              <div className="rounded-xl p-6" style={{ background: "#fbfbf4", border: "1px solid var(--line)" }}>
                <span className="eyebrow text-[9px] text-[#5c6b5a]">Registrations · last 14 days</span>
                <div className="mt-4 h-56" data-testid="admin-chart">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={stats.series}>
                      <XAxis dataKey="day" tickFormatter={(d) => d.slice(5)} tick={{ fontSize: 10, fill: "#5c6b5a" }} />
                      <YAxis allowDecimals={false} tick={{ fontSize: 10, fill: "#5c6b5a" }} width={24} />
                      <Tooltip cursor={{ fill: "rgba(215,239,139,.4)" }} contentStyle={{ borderRadius: 12, border: "1px solid rgba(18,61,48,.2)", fontSize: 12 }} />
                      <Bar dataKey="count" fill="#123d30" radius={[6, 6, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
              <div className="rounded-xl p-6" style={{ background: "#fbfbf4", border: "1px solid var(--line)" }}>
                <span className="eyebrow text-[9px] text-[#5c6b5a]">Paid by package</span>
                <div className="mt-4 grid gap-2" data-testid="admin-by-package">
                  {Object.entries(stats.by_package).length === 0 && <p className="text-[13px] text-[#5c6b5a]">No paid registrations yet.</p>}
                  {Object.entries(stats.by_package).map(([pkg, count]) => (
                    <div key={pkg} className="flex items-center justify-between text-[13px]">
                      <span>{pkg}</span>
                      <strong>{count}</strong>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {tab === "registrations" && (
          <div data-testid="admin-registrations">
            <div className="mb-4 flex flex-wrap items-center gap-3">
              <input data-testid="registrations-search" className="field-input !w-auto flex-1" placeholder="Search reference, name, email, company…" value={query} onChange={(e) => setQuery(e.target.value)} />
              <select data-testid="registrations-status-filter" className="field-input !w-auto" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                <option value="">All statuses</option>
                <option value="paid">Paid</option>
                <option value="awaiting_payment">Awaiting payment</option>
                <option value="awaiting_bank_transfer">Bank transfer</option>
                <option value="interest">Interest</option>
              </select>
              <button data-testid="registrations-export" onClick={() => exportCsv("export/registrations.csv")} className="ofic-btn ofic-btn-forest !px-4 !py-2 text-[12px]">Export CSV ↓</button>
            </div>
            <div className="overflow-x-auto rounded-xl" style={{ background: "#fbfbf4", border: "1px solid var(--line)" }}>
              <table className="w-full min-w-[860px]" data-testid="registrations-table">
                <thead className="border-b" style={{ borderColor: "var(--line)" }}>
                  <tr><Th>Reference</Th><Th>Date</Th><Th>Package</Th><Th>Pax</Th><Th>Amount</Th><Th>Method</Th><Th>Status</Th><Th>Contact</Th></tr>
                </thead>
                <tbody>
                  {filteredRegistrations.length === 0 && <tr><td className="px-3 py-8 text-center text-[13px] text-[#5c6b5a]" colSpan={8}>No registrations yet — they will appear here as soon as people register.</td></tr>}
                  {filteredRegistrations.map((r) => (
                    <tr key={r.id} className="cursor-pointer border-b transition-colors hover:bg-[#f2f4e4]" style={{ borderColor: "var(--line)" }} onClick={() => api.get(`/admin/registrations/${r.id}`).then(({ data }) => setDetail(data))} data-testid={`registration-row-${r.id}`}>
                      <Td className="font-bold">{r.id}</Td>
                      <Td>{(r.created || "").slice(0, 10)}</Td>
                      <Td>{r.package_name}</Td>
                      <Td>{r.quantity}</Td>
                      <Td>{r.amount ? money(r.amount / 100, r.currency) : money(r.base, r.currency) + (r.currency === "INR" ? "+GST" : "")}</Td>
                      <Td>{r.paymentMethod === "online" ? "Online" : "Bank"}</Td>
                      <Td><StatusBadge status={r.status} /></Td>
                      <Td><strong>{r.name}</strong><br /><span className="text-[12px] text-[#5c6b5a]">{r.company}</span></Td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === "leads" && (
          <div data-testid="admin-leads">
            <div className="mb-4 flex justify-end">
              <button data-testid="leads-export" onClick={() => exportCsv("export/leads.csv")} className="ofic-btn ofic-btn-forest !px-4 !py-2 text-[12px]">Export CSV ↓</button>
            </div>
            <div className="overflow-x-auto rounded-xl" style={{ background: "#fbfbf4", border: "1px solid var(--line)" }}>
              <table className="w-full min-w-[700px]" data-testid="leads-table">
                <thead className="border-b" style={{ borderColor: "var(--line)" }}>
                  <tr><Th>Date</Th><Th>Name</Th><Th>Email</Th><Th>Phone</Th><Th>Company</Th><Th>Source</Th><Th>Downloads</Th></tr>
                </thead>
                <tbody>
                  {leads.length === 0 && <tr><td className="px-3 py-8 text-center text-[13px] text-[#5c6b5a]" colSpan={7}>No leads yet — brochure downloads will appear here.</td></tr>}
                  {leads.map((l) => (
                    <tr key={l.id} className="border-b" style={{ borderColor: "var(--line)" }} data-testid={`lead-row-${l.id}`}>
                      <Td>{(l.created || "").slice(0, 10)}</Td>
                      <Td className="font-bold">{l.name}</Td>
                      <Td>{l.email}</Td>
                      <Td>{l.phone || "—"}</Td>
                      <Td>{l.company || "—"}</Td>
                      <Td>{l.source}</Td>
                      <Td>{l.downloads}</Td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === "carts" && (
          <div data-testid="admin-carts">
            <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
              <p className="text-[13px] font-bold text-[#5c6b5a]" data-testid="carts-sequence-note">Automatic recovery sequence: 1 hour → 1 day → 3 days → 7 days → 14 days → 21 days. Stops the moment someone registers.</p>
              <button data-testid="carts-export" onClick={() => exportCsv("export/abandoned.csv")} className="ofic-btn ofic-btn-forest !px-4 !py-2 text-[12px]">Export CSV ↓</button>
            </div>
            <div className="overflow-x-auto rounded-xl" style={{ background: "#fbfbf4", border: "1px solid var(--line)" }}>
              <table className="w-full min-w-[820px]" data-testid="carts-table">
                <thead className="border-b" style={{ borderColor: "var(--line)" }}>
                  <tr><Th>Last activity</Th><Th>Name</Th><Th>Email</Th><Th>Interest</Th><Th>Shown total</Th><Th>Recovery</Th><Th>Action</Th></tr>
                </thead>
                <tbody>
                  {carts.length === 0 && <tr><td className="px-3 py-8 text-center text-[13px] text-[#5c6b5a]" colSpan={7}>No abandoned carts yet.</td></tr>}
                  {carts.map((c) => {
                    const sent = (c.followups_sent || []).length;
                    return (
                      <tr key={c.id} className="border-b" style={{ borderColor: "var(--line)" }} data-testid={`cart-row-${c.id}`}>
                        <Td>{(c.last_activity || "").slice(0, 16).replace("T", " ")}</Td>
                        <Td className="font-bold">{c.name || "—"}</Td>
                        <Td>{c.email}</Td>
                        <Td>{c.package_name} · {c.participants} pax</Td>
                        <Td>{c.total_display || "—"}</Td>
                        <Td>
                          <StatusBadge status={c.converted ? "paid" : "interest"} />
                          <small className="mt-1 block text-[11px] text-[#5c6b5a]" data-testid={`cart-progress-${c.id}`}>
                            {c.converted ? "Converted ✓" : `${sent}/6 sent · ${nextDue(c)}`}
                          </small>
                        </Td>
                        <Td>
                          {!c.converted && (
                            <button data-testid={`cart-followup-${c.id}`} onClick={() => sendFollowup(c.id)} disabled={sent >= 6} className="rounded-full px-3 py-1.5 text-[11px] font-extrabold" style={{ background: "var(--forest)", color: "var(--lime)" }}>
                              {sent >= 6 ? "Done" : "Send next now"}
                            </button>
                          )}
                        </Td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </main>

      {detail && (
        <div data-testid="registration-detail-overlay" className="fixed inset-0 z-[70] grid place-items-center bg-[#0b2b21]/70 p-4" onClick={() => setDetail(null)}>
          <div data-testid="registration-detail" className="max-h-[85vh] w-full max-w-2xl overflow-y-auto rounded-2xl bg-[#f3f3e9] p-7" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between">
              <div>
                <span className="eyebrow text-[9px] text-[#5c6b5a]">Registration detail</span>
                <h3 className="font-display text-2xl">{detail.id}</h3>
              </div>
              <button data-testid="registration-detail-close" onClick={() => setDetail(null)} className="text-2xl leading-none opacity-60 hover:opacity-100">✕</button>
            </div>
            <div className="mt-4"><StatusBadge status={detail.status} /></div>
            <div className="mt-5 grid gap-2 text-[13px] sm:grid-cols-2">
              {[["Package", detail.package_name], ["Category", detail.currency === "INR" ? "Indian · INR" : "International · USD"], ["Company", detail.company], ["Country / City", `${detail.country || "—"} / ${detail.city || "—"}`], ["Contact", `${detail.name} · ${detail.email} · ${detail.phone}`], ["GSTIN", detail.gstin || "—"], ["Base fee", money(detail.base || 0, detail.currency)], ["GST", detail.taxAmount != null ? money(detail.taxAmount / 100, detail.currency) : "—"], ["Amount", detail.amount ? money(detail.amount / 100, detail.currency) : "—"], ["Payment", detail.payment || "—"], ["Accommodation", detail.accommodation?.requested ? `${detail.accommodation.rooms} room(s)${detail.accommodation.checkIn ? ` · ${detail.accommodation.checkIn} → ${detail.accommodation.checkOut}` : ""}` : "Not requested"]].map(([k, v]) => (
                <div key={k} className="rounded-lg p-3" style={{ background: "#fbfbf4", border: "1px solid var(--line)" }}>
                  <span className="eyebrow text-[8px] text-[#5c6b5a]">{k}</span>
                  <p className="mt-1 font-bold">{v}</p>
                </div>
              ))}
            </div>
            <h4 className="mt-6 font-display text-lg">Delegates ({(detail.delegates || []).length})</h4>
            <div className="mt-3 grid gap-2" data-testid="registration-detail-delegates">
              {(detail.delegates || []).map((d, i) => (
                <div key={i} className="rounded-lg p-3 text-[13px]" style={{ background: "#fbfbf4", border: "1px solid var(--line)" }}>
                  <strong>{d.firstName} {d.lastName}</strong> · {d.designation}
                  <br />
                  <span className="text-[12px] text-[#5c6b5a]">{d.email} · {d.phone}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
